import React, { useCallback, useState } from 'react';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import BeaconComparableSplineChart from 'src/components/BeaconV2Components/BeaconMetricSplineChart/BeaconComparableSplineChart';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import AggregationBuilder from 'src/components/BeaconV2Components/utils/AggregationBuilder';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';
import { shouldShowBeaconProTopics } from 'src/utils/app';
import { useComparisonTimePeriodShortDisplayName, useMainTimePeriodShortDisplayName } from 'src/utils/Hooks';
import { useMainEntityType, useProductEntity } from 'src/utils/Hooks/reduxSelectors';

const options = [
  {
    id: 'impressions',
    label: 'Ad Impressions'
  },
  {
    id: 'clickThroughRate',
    label: 'CTR %'
  },
  {
    id: 'spend',
    label: 'Ad Spend'
  },
  {
    id: 'costPerClick',
    label: 'CPC'
  },
  {
    id: 'costPerMilli',
    label: 'CPM'
  },
  {
    id: 'costPerAcquisition',
    label: 'CPA'
  },
  {
    id: 'atc',
    label: 'ATC'
  },
  {
    id: 'dpv',
    label: 'DPV'
  },
  {
    id: 'sales',
    label: 'Ad Sales'
  },
  {
    id: 'conversionRate',
    label: 'Conversion Rate'
  },
  {
    id: 'purchases',
    label: 'Purchases'
  },
  {
    id: 'unitsSold',
    label: 'Ad Units Sold'
  },
  {
    id: 'returnOnAdSpend',
    label: 'ROAS'
  },
  {
    id: 'stacklineSku',
    label: 'Products with Advertising'
  }
];
/**
 * Displays the traffic trend chart in the traffic display tab
 */
const TrafficDisplayTrendChart = () => {
  const [fieldName, setFieldName] = useState('impressions');
  const { isProductEntity } = useProductEntity();
  const mainPeriodDisplayName = useMainTimePeriodShortDisplayName();
  const comparisonDisplayName = useComparisonTimePeriodShortDisplayName();
  const mainEntityType = useMainEntityType();
  const isCategoryEntity = mainEntityType === 'category';
  const isSubcategoryEntity = mainEntityType === 'subcategory';
  const titleOnly = !(isProductEntity || isCategoryEntity || isSubcategoryEntity);
  const entityPhrase = useEntityPhrasing({ titleOnly });

  const taxonomyText = useTaxonomySubtitleText();
  const aggregationBuilder = useCallback(
    (aggBuilder: AggregationBuilder): AggregationBuilder => {
      if (fieldName === 'stacklineSku') {
        return aggBuilder.addConditionRangeFilter('clicks', 1);
      }
      return aggBuilder;
    },
    [fieldName]
  );

  const getSubtitle = () => {
    const displayName = options.find((option) => option.id === fieldName)?.label ?? '';
    const metricName = ['ROAS', 'CPA', 'CPM', 'CPC', 'CTR %', 'DPV', 'ATC'].includes(displayName)
      ? displayName
      : displayName.toLowerCase();
    return (
      <BeaconSubtitleContainer>
        <Text variant="body1">
          {`The chart displays the ${metricName} for ${entityPhrase}`}
          {taxonomyText}
          {` during the ${mainPeriodDisplayName} period compared to the ${comparisonDisplayName}.`}
          <SubtitleFilters />
        </Text>
      </BeaconSubtitleContainer>
    );
  };

  return (
    <BeaconComparableSplineChart
      beaconChartWithLegendOverride={
        shouldShowBeaconProTopics()
          ? (props) => {
              return {
                ...props,
                includeSummaryInfo: false,
                subtitle: getSubtitle()
              };
            }
          : undefined
      }
      indexName="advertisingDisplay"
      fieldName={fieldName}
      primaryAggregationBuilder={aggregationBuilder}
      secondaryAggregationBuilder={aggregationBuilder}
      singleDropdownProps={{
        options,
        defaultLabel: '',
        onChange: (option) => setFieldName(option.id),
        selectedId: fieldName
      }}
    />
  );
};

export default TrafficDisplayTrendChart;
